/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

class MarqueeTicker extends HTMLElement {
  connectedCallback() {
    this._build();
    this._resizeObserver?.disconnect();

    if (window.ResizeObserver) {
      this._lastWidth = this.clientWidth;
      this._resizeObserver = new ResizeObserver(() => {
        const width = this.clientWidth;

        if (Math.abs(width - this._lastWidth) > 40) {
          this._lastWidth = width;
          this._build();
        }
      });
      this._resizeObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this._resizeObserver?.disconnect();
  }

  _build() {
    const content = this.querySelector(".marquee-ticker__content");
    const inner = this.querySelector(".marquee-ticker__inner");

    if (!content || !inner) {
      return;
    }

    const chunkHTML = content.innerHTML.trim();

    if (!chunkHTML) {
      return;
    }

    const containerWidth = this.clientWidth || window.innerWidth;
    const track = document.createElement("div");
    track.classList.add("marquee-ticker__track");
    track.setAttribute("aria-hidden", "false");
    track.innerHTML = chunkHTML;

    inner.replaceChildren(track);

    while (track.scrollWidth < containerWidth) {
      track.insertAdjacentHTML("beforeend", chunkHTML);
    }

    const clone = track.cloneNode(true);
    clone.setAttribute("aria-hidden", "true");
    inner.appendChild(clone);
  }
}

window.customElements.define("marquee-ticker", MarqueeTicker);

class MenuPanelTrigger extends HTMLButtonElement {
  connectedCallback() {
    this.boundHandleClick = this._handleClick.bind(this);
    this.addEventListener('click', this.boundHandleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.boundHandleClick);
  }

  _handleClick() {
    const panelId = this.getAttribute('aria-controls');
    const panel = document.getElementById(panelId);
    if (panel) panel.open();
  }
}

customElements.define('menu-panel-trigger', MenuPanelTrigger, { extends: 'button' });

class MobileMenuPanel extends HTMLElement {
  connectedCallback() {
    const nav = this.closest('mobile-navigation');
    const drawerContent = nav && nav.querySelector(':scope > .drawer__content');
    const target = drawerContent || nav;

    if (target && this.parentElement !== target) {
      target.appendChild(this);
      return;
    }

    if (this._initialized) return;
    this._initialized = true;

    this._backButton = this.querySelector(':scope > .mobile-menu-panel__header [data-action="back"]');
    if (this._backButton) {
      this.boundHandleBack = this._handleBack.bind(this);
      this._backButton.addEventListener('click', this.boundHandleBack);
    }
  }

  disconnectedCallback() {
    if (this._backButton && this.boundHandleBack) {
      this._backButton.removeEventListener('click', this.boundHandleBack);
    }
    this._initialized = false;
  }

  open() {
    this.setAttribute('open', '');
    this.scrollTop = 0;
  }

  close() {
    this.removeAttribute('open');
  }

  _handleBack() {
    this.close();
  }
}

customElements.define('mobile-menu-panel', MobileMenuPanel);

class ProductDescriptionExpander extends HTMLElement {
  static get observedAttributes() {
    return ['open'];
  }

  get open() {
    return this.hasAttribute('open');
  }

  set open(value) {
    this.toggleAttribute('open', Boolean(value));
  }

  connectedCallback() {
    this._content = this.querySelector('.product-form__description-content');
    this._toggle = this.querySelector('.product-form__description-toggle');

    if (!this._content || !this._toggle) {
      return;
    }

    this._scheduleUpdate();

    if (window.ResizeObserver) {
      this._resizeObserver = new ResizeObserver(() => this._scheduleUpdate());
      this._resizeObserver.observe(this._content);
    }
  }

  disconnectedCallback() {
    this._resizeObserver?.disconnect();
    this._animation?.cancel();
    cancelAnimationFrame(this._raf);
  }

  attributeChangedCallback(name) {
    if (name !== 'open' || !this._content || !this._toggle) {
      return;
    }

    this._toggle.textContent = this.open
      ? this._toggle.dataset.showLess
      : this._toggle.dataset.showMore;

    this._animate();
  }

  _scheduleUpdate() {
    if (this.open || this._animating) {
      return;
    }

    cancelAnimationFrame(this._raf);
    this._raf = requestAnimationFrame(() => this._update());
  }

  _update() {
    this._collapsedHeight = this._content.clientHeight;
    this._toggle.hidden = this._content.scrollHeight <= this._content.clientHeight + 1;
  }

  _animate() {
    const content = this._content;
    const collapsedHeight = this._collapsedHeight || content.clientHeight;
    const currentHeight = content.getBoundingClientRect().height;

    content.style.display = 'block';
    content.style.webkitLineClamp = 'unset';
    content.style.lineClamp = 'unset';
    content.style.overflow = 'hidden';
    content.style.height = `${currentHeight || collapsedHeight}px`;

    this._animation?.cancel();
    this._animating = true;

    const startHeight = content.getBoundingClientRect().height || collapsedHeight;
    content.offsetHeight;

    const endHeight = this.open ? content.scrollHeight : collapsedHeight;

    this._animation = content.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      {
        duration: 500,
        easing: 'cubic-bezier(0.75, 0, 0.175, 1)',
      }
    );

    const onComplete = () => {
      this._animating = false;
      this._animation = null;

      if (this.open) {
        content.style.height = 'auto';
        content.style.overflow = 'visible';
      } else {
        content.removeAttribute('style');
        this._update();
      }
    };

    this._animation.onfinish = onComplete;
    this._animation.oncancel = () => {
      this._animating = false;
      this._animation = null;
    };

    this.dispatchEvent(
      new CustomEvent(this.open ? 'openable-element:open' : 'openable-element:close', {
        bubbles: true,
      })
    );
  }
}

customElements.define('product-description-expander', ProductDescriptionExpander);

class QuantityBreaks extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.quantity-selector__input');
    this._onChange = this._onChange.bind(this);
    this.addEventListener('change', this._onChange);
    queueMicrotask(() => this._syncFromInput());
  }

  disconnectedCallback() {
    this.removeEventListener('change', this._onChange);
  }

  _onChange(event) {
    const radio = event.target.closest('.quantity-break__radio');

    if (!radio || !this.input) {
      return;
    }

    this.input.quantity = radio.value;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    this._updatePrice();
  }

  _syncFromInput() {
    if (!this.input) {
      return;
    }

    const value = String(this.input.value);

    this.querySelectorAll('.quantity-break__radio').forEach((radio) => {
      radio.checked = radio.value === value;
    });

    this._updatePrice();
  }

  _updatePrice() {
    const productForm = this.closest('.product-form');
    const priceList = productForm?.querySelector('[data-quantity-price]');
    const radio = this.querySelector('.quantity-break__radio:checked');

    if (!priceList || !radio || typeof formatMoney !== 'function') {
      return;
    }

    const quantity = Number(radio.value);
    const percentage = Number(radio.dataset.percentage) || 0;
    const renderedQuantity = Number(priceList.dataset.renderedQuantity);
    const renderedPercentage = Number(priceList.dataset.renderedPercentage) || 0;

    if (this._appliedQuantity === quantity && this._appliedPercentage === percentage) {
      return;
    }

    if (this._appliedQuantity == null && quantity === renderedQuantity && percentage === renderedPercentage) {
      this._appliedQuantity = quantity;
      this._appliedPercentage = percentage;
      return;
    }

    const unitPrice = Number(priceList.dataset.unitPrice);
    const unitComparePrice = Number(priceList.dataset.unitComparePrice) || 0;
    const unitMeasurementPrice = Number(priceList.dataset.unitMeasurementPrice) || 0;

    if (!quantity || !Number.isFinite(unitPrice)) {
      return;
    }

    const settings = window.themeVariables?.settings || {};
    const moneyFormat = settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat;
    const discountPerUnit = Math.floor((unitPrice * percentage) / 100);
    const quantityPrice = (unitPrice - discountPerUnit) * quantity;
    const comparePrice = unitComparePrice > unitPrice ? unitComparePrice * quantity : percentage > 0 ? unitPrice * quantity : 0;
    const showCompare = comparePrice > quantityPrice;
    const saleAmount = priceList.querySelector('[data-quantity-sale-price]');
    const comparePriceEl = priceList.querySelector('[data-quantity-compare-price]');
    const compareAmount = priceList.querySelector('[data-quantity-compare-amount]');
    const salePrice = saleAmount?.closest('.price');

    if (saleAmount) {
      saleAmount.textContent = formatMoney(quantityPrice, moneyFormat);
    }

    salePrice?.classList.toggle('price--highlight', showCompare);

    if (comparePriceEl) {
      comparePriceEl.hidden = !showCompare;

      if (compareAmount && showCompare) {
        compareAmount.textContent = formatMoney(comparePrice, moneyFormat);
      }
    }

    const unitPriceEl = priceList.querySelector('[data-quantity-unit-price]');

    if (unitPriceEl && unitMeasurementPrice) {
      const measurementDiscount = Math.floor((unitMeasurementPrice * percentage) / 100);
      unitPriceEl.textContent = formatMoney(unitMeasurementPrice - measurementDiscount, settings.moneyFormat);
    }

    const dailyBadge = productForm.querySelector('[data-daily-dose-price]');

    if (dailyBadge) {
      const contentAmount = Number(dailyBadge.dataset.contentAmount);
      const dailyDose = Number(dailyBadge.dataset.dailyDose);
      const discountedUnit = unitPrice - discountPerUnit;

      if (contentAmount > 0 && dailyDose > 0 && discountedUnit > 0) {
        const dailyCents = Math.round((discountedUnit * dailyDose) / contentAmount);
        const formatted = formatMoney(dailyCents, settings.moneyFormat);
        const template = window.themeVariables?.strings?.productDailyPrice || '';

        if (template) {
          dailyBadge.textContent = template.replace('@price@', formatted);
        }
      }
    }

    const discountEl = productForm.querySelector('[data-quantity-discount]');

    if (discountEl) {
      if (!showCompare) {
        discountEl.hidden = true;
      } else {
        const savings =
          settings.discountMode === 'percentage'
            ? `${Math.round(((comparePrice - quantityPrice) * 100) / comparePrice)}%`
            : formatMoney(comparePrice - quantityPrice, settings.moneyFormat);
        const template = window.themeVariables?.strings?.collectionDiscount || '';

        discountEl.textContent = template.replace('@savings@', savings);
        discountEl.hidden = false;
      }
    }

    this._appliedQuantity = quantity;
    this._appliedPercentage = percentage;
  }
}

if (!window.customElements.get('quantity-breaks')) {
  window.customElements.define('quantity-breaks', QuantityBreaks);
}

class BackInStock extends HTMLElement {
  connectedCallback() {
    if (this._initialized) {
      return;
    }

    this._modal = this.querySelector('modal-content');
    this._form = this.querySelector('form');
    this._title = this.querySelector('h2');
    this._fields = this._form?.querySelector(':scope > div');
    this._variantSelect = this._form?.elements.variant;
    this._variantField = this._variantSelect?.closest('.input');
    this._emailInput = this._form?.elements.email;
    this._newsletterInput = this._form?.elements.newsletter;
    this._error = this.querySelector('[role="alert"]');
    this._success = this.querySelector('[role="status"]');
    this._submitButton = this.querySelector('button[type="submit"]');

    if (!this._modal || !this._form) {
      return;
    }

    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._onEmailInput = this._onEmailInput.bind(this);
    this._onSubmit = this._onSubmit.bind(this);

    document.addEventListener('click', this._onDocumentClick);
    this._emailInput?.addEventListener('input', this._onEmailInput);
    this._form.addEventListener('submit', this._onSubmit);
    this._initialized = true;
  }

  disconnectedCallback() {
    if (!this._initialized) {
      return;
    }

    document.removeEventListener('click', this._onDocumentClick);
    this._emailInput?.removeEventListener('input', this._onEmailInput);
    this._form?.removeEventListener('submit', this._onSubmit);
    this._initialized = false;
  }

  _onDocumentClick(event) {
    const button = event.target.closest('[aria-controls="back-in-stock"]');

    if (!button) {
      return;
    }

    event.preventDefault();
    this.open(button);
  }

  _onEmailInput() {
    this._emailInput.classList.toggle('is-filled', this._emailInput.value.trim() !== '');
  }

  open(button) {
    let variants = [];

    try {
      variants = JSON.parse(button.getAttribute('data-variants') || '[]');
    } catch (error) {
      variants = [];
    }

    if (!variants.length) {
      return;
    }

    this._reset();
    this._title.textContent = button.getAttribute('data-product-title') || '';
    this._variantSelect.replaceChildren();

    variants.forEach((variant) => {
      const option = document.createElement('option');
      option.value = String(variant.id);
      option.textContent = variant.title;
      this._variantSelect.append(option);
    });

    const showVariants = variants.length > 1 && variants.some((variant) => variant.title && variant.title !== 'Default Title');
    this._variantField.hidden = !showVariants;
    this._variantSelect.required = showVariants;
    this._modal.open = true;

    window.setTimeout(() => {
      (showVariants ? this._variantSelect : this._emailInput).focus();
    }, 200);
  }

  async _onSubmit(event) {
    event.preventDefault();
    this._error.hidden = true;

    const email = this._emailInput.value.trim();
    const variantId = this._variantSelect.value;

    if (!email || !variantId || !this._emailInput.checkValidity()) {
      this._emailInput.reportValidity();
      return;
    }

    this._submitButton.disabled = true;
    this._submitButton.setAttribute('aria-busy', 'true');

    try {
      const companyId = this.dataset.companyId;
      const response = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(companyId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2025-10-15',
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email },
                },
              },
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${variantId}`,
                },
              },
            },
          },
        }),
      });

      if (response.status !== 202) {
        const errorText = await response.text();
        const alreadySubscribed = response.status === 409 || /already|duplicate|exists/i.test(errorText);

        if (!alreadySubscribed) {
          throw new Error(errorText);
        }
      }

      if (this._newsletterInput.checked && this.dataset.listId) {
        fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: '2025-10-15',
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Back in stock',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      subscriptions: {
                        email: {
                          marketing: {
                            consent: 'SUBSCRIBED',
                          },
                        },
                      },
                    },
                  },
                },
              },
              relationships: {
                list: {
                  data: {
                    type: 'list',
                    id: this.dataset.listId,
                  },
                },
              },
            },
          }),
        }).catch(() => {});
      }

      this._fields.hidden = true;
      this._success.hidden = false;
    } catch (error) {
      this._error.hidden = false;
      this._submitButton.disabled = false;
    } finally {
      this._submitButton.removeAttribute('aria-busy');
    }
  }

  _reset() {
    this._form.reset();
    this._emailInput.classList.remove('is-filled');
    this._error.hidden = true;
    this._success.hidden = true;
    this._fields.hidden = false;
    this._submitButton.disabled = false;
    this._submitButton.removeAttribute('aria-busy');
  }
}

if (!window.customElements.get('back-in-stock')) {
  window.customElements.define('back-in-stock', BackInStock);
}

class BentoGallery extends HTMLElement {
  connectedCallback() {
    if (window.Shopify && Shopify.designMode && !this._onSectionSelect) {
      this._hasSectionReloaded = false;
      this._onSectionSelect = (event) => {
        const parentSection = this.closest('.shopify-section');

        if (event.target === parentSection && event.detail.load) {
          this._hasSectionReloaded = true;
        }
      };

      document.addEventListener('shopify:section:select', this._onSectionSelect);
    }

    if (this.hasAttribute('stagger-apparition') && !this._visibilityReady) {
      this._visibilityReady = true;
      this._setupVisibility();
    }
  }

  disconnectedCallback() {
    this._intersectionObserver?.disconnect();
    this._visibilityReady = false;

    if (this._onSectionSelect) {
      document.removeEventListener('shopify:section:select', this._onSectionSelect);
      this._onSectionSelect = null;
    }
  }

  _untilVisible() {
    const threshold = this.clientHeight > 0 ? Math.min(50 / this.clientHeight, 1) : 0;

    return new Promise((resolve) => {
      if (!window.IntersectionObserver) {
        resolve();
        return;
      }

      this._intersectionObserver = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this._intersectionObserver.disconnect();
          requestAnimationFrame(() => resolve());
        }
      }, { threshold });

      this._intersectionObserver.observe(this);
    });
  }

  async _setupVisibility() {
    const items = Array.from(this.querySelectorAll('.bento-gallery__item'));

    if (!items.length) {
      return;
    }

    await this._untilVisible();

    if (!this.isConnected) {
      return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const distance = prefersReducedMotion ? 0 : window.innerWidth < 1000 ? 35 : 60;
    const animations = items.map((item, index) => {
      return item.animate(
        {
          opacity: [0, 1],
          transform: [`translateY(${distance}px)`, 'translateY(0)'],
        },
        {
          duration: 600,
          delay: prefersReducedMotion ? 0 : 100 * index,
          easing: 'ease',
          fill: 'forwards',
        }
      );
    });

    if (this._hasSectionReloaded) {
      animations.forEach((animation) => animation.finish());
    }
  }
}

if (!window.customElements.get('bento-gallery')) {
  window.customElements.define('bento-gallery', BentoGallery);
}